// SCADA entegrasyon ekranının sunum kuralları — saf fonksiyonlar.
//
// Ayrı dosyada duruyorlar çünkü hepsi sözleşmeye (docs/ENTEGRASYON.md) bağlı
// birer karar: yaş eşiği, kalite sözlüğü ve "ham metni yeniden biçimlendirme"
// yasağı. Bileşenin içine gömülselerdi test edilemezlerdi.

import { tr } from '../../lib/trFormat'
import type { EtiketSatiri, PaketTuru } from './api'

/**
 * Yaş (sn) → okunur metin. Yaş SUNUCUDAN geliyor (Dto.cs): "ajan sustu mu"
 * sorusunun ölçüsü sunucu saatidir; istemci kendi saatinden hesaplasaydı saati
 * şaşmış bir tarayıcı sahte sessizlik ya da sahte canlılık gösterirdi.
 */
export function yasMetni(saniye: number | null | undefined): string {
  if (saniye === null || saniye === undefined) return '—'
  const s = Math.max(0, Math.round(saniye))
  if (s < 60) return `${tr(s, 0)} sn`
  if (s < 3600) return `${tr(Math.floor(s / 60), 0)} dk`
  if (s < 86_400) return `${tr(Math.floor(s / 3600), 0)} sa`
  return `${tr(Math.floor(s / 86_400), 0)} gün`
}

export type AkisDurumu = { etiket: string; sinif: string; baslik: string }

/**
 * Akış sağlığı — sözleşme §5: "site başına son paket yaşı > 3× periyot" arıza
 * sinyalidir. Periyot AJAN konfigürasyonunda (5–300 sn, tipik 30 sn); sunucu
 * onu bilmiyor, dolayısıyla biz de bilmiyoruz. Bu yüzden eşik en uzun periyodun
 * üçüncü katına göre konuldu: 900 sn'ye kadar susmak her konfigürasyonda
 * normal olabilir, üstü hiçbirinde normal değil. 120 sn ise tipik periyodun
 * (30 sn) dört katı — altındaki her şey kesinlikle canlıdır.
 */
export function akisDurumu(yasSn: number | null | undefined): AkisDurumu {
  if (yasSn === null || yasSn === undefined)
    return { etiket: 'Veri yok', sinif: 'b-gray', baslik: 'Bu kaynaktan hiç paket gelmedi' }
  if (yasSn <= 120)
    return { etiket: 'Canlı', sinif: 'b-navy', baslik: `Son paket ${yasMetni(yasSn)} önce` }
  if (yasSn <= 900)
    return {
      etiket: 'Gecikmiş',
      sinif: 'b-amber',
      baslik: `Son paket ${yasMetni(yasSn)} önce — ajan periyoduna göre normal olabilir`,
    }
  return {
    etiket: 'Sessiz',
    sinif: 'b-red',
    baslik: `Son paket ${yasMetni(yasSn)} önce — makine/ağ arızası olabilir (§5)`,
  }
}

/**
 * OCR kalitesi (§3.2) → rozet. Küme sabitlenmiyor: sözleşme §3.6 bilinmeyen
 * değerin yok sayılmasını istiyor, biz de tanımadığımızı olduğu gibi basıyoruz
 * — ajan yeni bir kalite adı üretirse ekranda "—" değil kendi adı görünsün.
 */
export function kaliteRozeti(kalite: string | null | undefined): {
  etiket: string
  sinif: string
} {
  switch (kalite) {
    case 'good':
      return { etiket: 'İyi', sinif: 'b-navy' }
    case 'unreadable':
      return { etiket: 'Okunamadı', sinif: 'b-red' }
    case 'out_of_range_low':
      return { etiket: 'Sınır altı', sinif: 'b-amber' }
    case 'out_of_range_high':
      return { etiket: 'Sınır üstü', sinif: 'b-amber' }
    case 'state':
      return { etiket: 'Durum', sinif: 'b-gray' }
    case null:
    case undefined:
    case '':
      return { etiket: '—', sinif: 'b-gray' }
    default:
      return { etiket: kalite, sinif: 'b-gray' }
  }
}

/**
 * Etiketin gösterilecek son değeri.
 *
 * Sözleşme §3.2 açıkça diyor: `v` ekrandan okunan HAM metindir, TR ondalık
 * virgül taşır ve "kullanıcıya gösterim için" saklanır; `num` ise hesap içindir.
 * Bu yüzden burada YENİDEN BİÇİMLENDİRME YAPILMIYOR — operatör SCADA ekranında
 * ne görüyorsa panelde de onu görmeli. `tr()` ile yeniden basmak "1400,0"ı
 * "1.400" yapar ve iki ekran birbirini tutmaz.
 *
 * Ham metin yoksa (yalnız `num` geldiyse) tek çare sayıyı basmaktır; o da
 * §12 gereği trFormat üzerinden geçer.
 */
export function sonDegerMetni(e: Pick<EtiketSatiri, 'sonHam' | 'sonSayi'>): string {
  if (e.sonHam !== null && e.sonHam !== undefined && e.sonHam.trim() !== '') return e.sonHam
  if (e.sonSayi !== null && e.sonSayi !== undefined) return tr(e.sonSayi, 3)
  return '—'
}

/** Ölçüm noktası için aynı kural — seri listesinde de ham metin esastır. */
export function noktaMetni(n: { ham: string; sayi: number | null }): string {
  if (n.ham && n.ham.trim() !== '') return n.ham
  return n.sayi === null ? '—' : tr(n.sayi, 3)
}

export const PAKET_TURU_ETIKET: Record<PaketTuru, string> = {
  okuma: 'Okuma',
  bilinmeyen: 'Bilinmeyen sayfa',
  goruntu_yok: 'Görüntü yok',
  hata: 'Döngü hatası',
}

/** Okuma dışı paketler veri taşımaz ama "ajan yaşıyor" sinyalidir (§3.4). */
export const PAKET_TURU_SINIF: Record<PaketTuru, string> = {
  okuma: 'b-navy',
  bilinmeyen: 'b-amber',
  goruntu_yok: 'b-gray',
  hata: 'b-red',
}
